import React, { useEffect, useState } from 'react';
import {
    Card, CardBody, CardTitle, CardText, Button, Badge, Row, Col,
    Popover, PopoverHeader, PopoverBody
} from 'reactstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { callChequeDeposit, getChequeSummary } from '../util/APIUtils';
import { FaBell, FaExclamationTriangle, FaCheckCircle, FaHistory, FaClock, FaUniversity, FaTimesCircle } from 'react-icons/fa';

// Timeline Popover Component
const TimelinePopover = ({ id, timelineNote }) => {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const toggle = () => setPopoverOpen(!popoverOpen);

    const timelineSteps = timelineNote ? timelineNote.split('|').map(s => s.trim()).filter(Boolean) : [];

    // Return empty if no timeline data
    if (!timelineNote || timelineSteps.length === 0) {
        return null;
    }

    const getStepIcon = (step) => {
        if (step.toLowerCase().includes('deposited')) return <FaUniversity className="text-primary" />;
        if (step.toLowerCase().includes('returned')) return <FaTimesCircle className="text-danger" />;
        if (step.toLowerCase().includes('cleared')) return <FaCheckCircle className="text-success" />;
        return <FaClock className="text-secondary" />;
    };

    const getStepColor = (step) => {
        if (step.toLowerCase().includes('deposited')) return '#007bff';
        if (step.toLowerCase().includes('returned')) return '#dc3545';
        if (step.toLowerCase().includes('cleared')) return '#28a745';
        return '#6c757d';
    };

    return (
        <span>
            <Button
                id={`timeline-${id}`}
                color="link"
                size="sm"
                className="p-0"
                style={{ fontSize: 18 }}
                onClick={toggle}
            >
                <FaHistory className="text-info" />
                {timelineSteps.length > 0 && (
                    <Badge color="info" pill style={{ fontSize: 9, marginLeft: 4, verticalAlign: 'top' }}>
                        {timelineSteps.length}
                    </Badge>
                )}
            </Button>
            <Popover
                placement="left"
                isOpen={popoverOpen}
                target={`timeline-${id}`}
                toggle={toggle}
                trigger="legacy"
            >
                <PopoverHeader className="bg-light font-weight-bold" style={{ fontSize: 13 }}>
                    <FaHistory className="mr-2" />Cheque History
                </PopoverHeader>
                <PopoverBody style={{ padding: 0, maxHeight: 250, overflowY: 'auto' }}>
                    <div style={{ padding: '12px 16px' }}>
                        {timelineSteps.map((step, idx) => (
                            <div key={idx} className="d-flex align-items-start" style={{ marginBottom: idx < timelineSteps.length - 1 ? 16 : 0 }}>
                                <div className="d-flex flex-column align-items-center mr-3">
                                    <div style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        background: getStepColor(step),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontSize: 12
                                    }}>
                                        {getStepIcon(step)}
                                    </div>
                                    {idx < timelineSteps.length - 1 && (
                                        <div style={{
                                            width: 2,
                                            height: 20,
                                            background: '#dee2e6',
                                            marginTop: 4
                                        }} />
                                    )}
                                </div>
                                <div style={{ fontSize: 12, lineHeight: 1.4, paddingTop: 4 }}>
                                    {step}
                                </div>
                            </div>
                        ))}
                    </div>
                </PopoverBody>
            </Popover>
        </span>
    );
};



const statusColor = {
    OPEN: 'secondary',
    DEPOSITED: 'success',
    RETURNED: 'danger',
    CLEARED: 'primary'
};

function ChequeDashboard() {
    const [chequeData, setChequeData] = useState([]);

    useEffect(() => {
        refreshData();
    }, []);

    const refreshData = () => {
        getChequeSummary()
            .then((data) => {
                setChequeData(data.allCheques);
            })
            .catch(() => setChequeData([]));
    }

    const handleChequeDeposit = (row, action) => {
        row.action = action;
        callChequeDeposit(row).then(() => {
            swal("Success", `Cheque marked as ${action}.`, "success");
            refreshData();
        }).catch(() => {
            swal("Error", "Failed to mark cheque as deposited.", "error");
        });
    };
    // Count for summary cards
    const pendingCount = chequeData.filter(c => c.chequeStatus === 'OPEN').length;
    const returnedCount = chequeData.filter(c => c.chequeStatus === 'RETURNED').length;
    return (
        <div className="container-fluid py-3" style={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Row className="mb-3">
                <Col md={12}>
                    <Row>
                        <Col sm={6} className="mb-2">
                            <Card className="h-100 d-flex flex-row align-items-center p-2">
                                <FaBell size={36} className="mr-3 text-primary" />
                                <div>
                                    <CardTitle tag="h6" className="mb-1 font-weight-bold">Pending Cheques to Deposit</CardTitle>
                                    <CardText className="mb-0" style={{ fontSize: 22, fontWeight: 700 }}>{pendingCount} <span style={{ fontWeight: 400, fontSize: 16 }}>View details</span></CardText>
                                </div>
                            </Card>
                        </Col>
                        <Col sm={6} className="mb-2">
                            <Card className="h-100 d-flex flex-row align-items-center p-2">
                                <FaExclamationTriangle size={36} className="mr-3 text-danger" />
                                <div>
                                    <CardTitle tag="h6" className="mb-1 font-weight-bold">Returned Cheques</CardTitle>
                                    <CardText className="mb-0" style={{ fontSize: 22, fontWeight: 700 }}>{returnedCount} <span style={{ fontWeight: 400, fontSize: 16 }}>Penalty applied</span></CardText>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                    <Card className="mt-2">
                        <CardBody>
                            <BootstrapTable
                                data={chequeData}
                                version="4"
                                condensed
                                striped
                                hover
                                pagination
                                search
                                options={{
                                    sizePerPage: 5,
                                    paginationSize: 3,
                                    clearSearch: true,
                                    alwaysShowAllBtns: true,
                                    withFirstAndLast: true,
                                    searchFieldStyle: { fontSize: "10px", height: "30px" },
                                }}
                                csvFileName="cheque_summary.csv"
                            >
                                <TableHeaderColumn isKey dataField="id" hidden>ID</TableHeaderColumn>
                                <TableHeaderColumn dataField="invoiceNumber" dataSort filter={{ type: 'TextFilter', delay: 1000 }}>Invoice Number</TableHeaderColumn>
                                <TableHeaderColumn dataField="partyName" width="200" dataSort filter={{ type: 'TextFilter', delay: 1000 }}>Party Name</TableHeaderColumn>
                                <TableHeaderColumn dataField="chequeNumber" dataSort filter={{ type: 'TextFilter', delay: 1000 }}>Cheque No.</TableHeaderColumn>
                                <TableHeaderColumn dataField="chequeDate" dataSort filter={{ type: 'TextFilter', delay: 1000 }}>Cheque Date</TableHeaderColumn>
                                <TableHeaderColumn dataField="chequeStatus" dataSort filter={{ type: 'TextFilter', delay: 1000 }} dataFormat={(cell) => (
                                    <Badge color={statusColor[cell]} pill style={{ fontSize: 8, minWidth: 50 }}>{cell}</Badge>
                                )}>Status</TableHeaderColumn>
                                <TableHeaderColumn dataField="timelineNote" dataFormat={(cell, row) => (
                                    <TimelinePopover id={row.id} timelineNote={cell} />
                                )} width="80" dataAlign="center">History</TableHeaderColumn>
                                <TableHeaderColumn dataField="action" dataFormat={(cell, row) => {
                                    if (row.chequeStatus === 'OPEN') {
                                        return <Button color="primary" size="sm" onClick={() => handleChequeDeposit(row, 'DEPOSIT')}>Deposit</Button>;
                                    }
                                    if (row.chequeStatus === 'DEPOSITED') {
                                        return <div><Button color="success" size="sm" onClick={() => handleChequeDeposit(row, 'RETURNED')}>Returned</Button>
                                            <Button color="secondary" size="sm" className="ml-2" onClick={() => handleChequeDeposit(row, 'CLEARED')}>Cleared

                                            </Button>
                                        </div>;
                                    }
                                    return null;
                                }}>Action</TableHeaderColumn>
                            </BootstrapTable>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default ChequeDashboard;


// WEBPACK FOOTER //
// ./src/components/ChequeDashboard.js